import React, { useState, useEffect, useMemo } from "react";
import defaultAvatar from "../assets/default-avatar.png";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaGlobe,
  FaCheckCircle,
  FaEdit,
  FaSave,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa";
import { supabase } from "../utils/supabaseClient";
import { useLanguage } from "../components/LanguajeContext";
import { translations } from "../utils/profile.translate";

// Función para validar nombres y apellidos (solo letras y espacios, entre 2 y 50 caracteres)
const isValidName = (name) => {
  if (!name) return false;
  const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]{2,50}$/;
  return nameRegex.test(name.trim());
};

// Función para validar números de teléfono de Costa Rica
const isValidCostaRicaPhone = (phone) => {
  if (!phone) return false;
  
  // Eliminar espacios, guiones y paréntesis
  const cleanedPhone = phone.replace(/[\s\-\(\)]/g, '');
  
  // Patrones para números costarricenses:
  // - Móviles: 8 dígitos que comienzan con 5, 6, 7 u 8
  // - Fijos: 8 dígitos que comienzan con 2
  // - Con código de país: +506 o 506 seguido de 8 dígitos
  const mobilePattern = /^[5-8]\d{7}$/;
  const landlinePattern = /^2\d{7}$/;
  const countryCodePattern = /^(\+?506)?[5-8]\d{7}$/;
  const countryCodeLandlinePattern = /^(\+?506)?2\d{7}$/;
  
  return mobilePattern.test(cleanedPhone) || 
         landlinePattern.test(cleanedPhone) || 
         countryCodePattern.test(cleanedPhone) ||
         countryCodeLandlinePattern.test(cleanedPhone);
};

const formatCostaRicaPhone = (phone) => {
  if (!phone) return "";
  
  // Limpiar el número
  const cleanedPhone = phone.replace(/[\s\-\(\)]/g, '');
  
  // Si incluye código de país, extraer solo los últimos 8 dígitos
  let digitsOnly = cleanedPhone;
  if (cleanedPhone.startsWith('506')) {
    digitsOnly = cleanedPhone.substring(3);
  } else if (cleanedPhone.startsWith('+506')) {
    digitsOnly = cleanedPhone.substring(4);
  }
  
  if (digitsOnly.length === 8) {
    return `${digitsOnly.substring(0, 4)}-${digitsOnly.substring(4)}`;
  }
  
  return phone;
};


export default function Profile() {
  const [editMode, setEditMode] = useState(false);
  const [operator, setOperator] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [nameError, setNameError] = useState("");
  const [lastName1Error, setLastName1Error] = useState("");
  const [lastName2Error, setLastName2Error] = useState("");
  const [imageUrl, setImageUrl] = useState(() => {
    const cached = localStorage.getItem("cachedImage");
    if (!cached) return null;
    try {
      const parsed = JSON.parse(cached);
      return parsed.url || null;
    } catch {
      return null;
    }
  });

  const { language } = useLanguage() || { language: 'es' };
  const t = translations[language] || translations.es;

  const id_operator = localStorage.getItem("id_operator");
  //const token = localStorage.getItem("token");
  const URL = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    const cachedOperator = localStorage.getItem("cachedOperator");
    const cachedImage = localStorage.getItem("cachedImage");

    if (cachedOperator) {
      const parsedOp = JSON.parse(cachedOperator);
      setOperator(parsedOp);
      setFormData(parsedOp);

      if (cachedImage) {
        const parsedImg = JSON.parse(cachedImage);
        if (Date.now() < parsedImg.expiry && parsedImg.path === parsedOp.picture) {
          setImageUrl(parsedImg.url);
        }
      }
    }
  }, []);

  useEffect(() => {
    async function fetchOperator() {
      setLoading(true);
      try {
        if (!id_operator) throw new Error(t.noOperatorId);
        //if (!token) throw new Error(t.noToken);

        const res = await fetch(`${URL}/api/operator/profile/${id_operator}`, {
          headers: { 
            "Content-Type": "application/json" 
            //Authorization: `Bearer ${token}` 
            },
             credentials:"include"
        });

        if (!res.ok) throw new Error(t.errorFetchingData);
        const data = await res.json();

        setOperator(data);
        setFormData(data);
       // localStorage.setItem("cachedOperator", JSON.stringify(data));
        if(data.picture){
          setImageUrl(data.picture)

        }
        else{
          setImageUrl(null)

        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchOperator();
  }, [id_operator, language]); 

  const fullName = useMemo(() => {
    return operator ? `${operator.name} ${operator.last_name1} ${operator.last_name2}` : "";
  }, [operator]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Validación en tiempo real
    if (name === "phone") {
      if (value && !isValidCostaRicaPhone(value)) {
        setPhoneError(t.invalidPhone);
      } else {
        setPhoneError("");
      }
    } else if (name === "name") {
      if (value && !isValidName(value)) {
        setNameError(t.invalidName);
      } else {
        setNameError("");
      }
    } else if (name === "last_name1") {
      if (value && !isValidName(value)) {
        setLastName1Error(t.invalidLastName);
      } else {
        setLastName1Error("");
      }
    } else if (name === "last_name2") {
      if (value && !isValidName(value)) {
        setLastName2Error(t.invalidLastName);
      } else {
        setLastName2Error("");
      }
    }
  };

  // Función para verificar si hay errores en el formulario
  const hasErrors = () => {
    return !!(
      phoneError || 
      nameError || 
      lastName1Error || 
      lastName2Error ||
      (formData.name && !isValidName(formData.name)) ||
      (formData.last_name1 && !isValidName(formData.last_name1)) ||
      (formData.last_name2 && !isValidName(formData.last_name2)) ||
      (formData.phone && !isValidCostaRicaPhone(formData.phone))
    );
  };

  const handleSave = async () => {
    // Validar todos los campos antes de guardar
    if (formData.phone && !isValidCostaRicaPhone(formData.phone)) {
      setPhoneError(t.invalidPhone);
      return;
    }
    
    if (formData.name && !isValidName(formData.name)) {
      setNameError(t.invalidName);
      return;
    }
    
    if (formData.last_name1 && !isValidName(formData.last_name1)) {
      setLastName1Error(t.invalidLastName);
      return;
    }
    
    if (formData.last_name2 && !isValidName(formData.last_name2)) {
      setLastName2Error(t.invalidLastName);
      return;
    }
    
    try {
      const dataToSave = {
        ...formData,
        phone: formatCostaRicaPhone(formData.phone)
      };
      
      const res = await fetch(`${URL}/api/operator/profile/update/${id_operator}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", 
         // Authorization: `Bearer ${token}` 
        },
        credentials:"include",
        body: JSON.stringify(dataToSave),
      });

      if (!res.ok) throw new Error(t.errorUpdating);

      const updatedRes = await fetch(`${URL}/api/operator/profile/${id_operator}`, {
        headers: { "Content-Type": "application/json",
          // Authorization: `Bearer ${token}` 
          },
           credentials:"include"
      });
      if (!updatedRes.ok) throw new Error(t.errorFetchingUpdated);

      const updatedOperator = await updatedRes.json();
      setOperator(updatedOperator);
      setFormData(updatedOperator);
      //localStorage.setItem("cachedOperator", JSON.stringify(updatedOperator));

      if (updatedOperator.picture) {
        setImageUrl(updatedOperator.picture);
      } else {
        setImageUrl(null);
      }

      setEditMode(false);
      setPhoneError(""); 
      setNameError("");
      setLastName1Error("");
      setLastName2Error("");
    } catch (error) {
      alert(error.message);
    }
  };

  const handleCancel = () => {
    setFormData(operator);
    setEditMode(false);
    setPhoneError(""); 
    setNameError("");
    setLastName1Error("");
    setLastName2Error("");
  };

  if (loading && !operator) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#102B3F] to-[#2D4A5E]">
        <div className="animate-pulse text-white text-lg">{t.loadingProfile}</div>
      </div>
    );
  }

  if (error)
    return (
      <div className="text-red-500 p-6">
        {t.error}: {error}
        {!id_operator && <p>{t.loginToViewProfile}</p>}
      </div>
    );

  if (!operator) return <div className="text-white p-6">{t.operatorNotFound}</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#102B3F] to-[#2D4A5E] flex items-center justify-center p-6">
      <div className="max-w-4xl w-full bg-white/10 backdrop-blur-md shadow-xl rounded-3xl p-8 flex flex-col md:flex-row gap-8 border border-white/20">
        {/* Avatar */}
        <div className="flex flex-col items-center gap-4 md:w-1/3">
          <div className="relative">
            <img
              loading="lazy"
              src={imageUrl || defaultAvatar}
              alt="Avatar"
              className="w-40 h-40 rounded-full border-4 border-white shadow-lg object-cover transition-opacity duration-300"
            />
            <div
              className="absolute bottom-0 right-0 bg-green-400 text-white p-1 rounded-full shadow-md cursor-pointer"
              onClick={() => setEditMode(true)}
            >
              <FaEdit className="h-4 w-4" />
            </div>
          </div>
          {editMode ? (
            <div className="flex flex-col gap-2 text-center w-full">
              <div className="relative">
                <input
                  name="name"
                  value={formData.name || ""}
                  onChange={handleChange}
                  placeholder={t.namePlaceholder}
                  className="bg-transparent border-b border-white p-1 text-white text-center w-full"
                />
                {nameError && (
                  <div className="text-red-400 text-xs mt-1 text-center">
                    {nameError}
                  </div>
                )}
              </div>
              <div className="relative">
                <input
                  name="last_name1"
                  value={formData.last_name1 || ""}
                  onChange={handleChange}
                  placeholder={t.lastName1Placeholder}
                  className="bg-transparent border-b border-white p-1 text-white text-center w-full"
                />
                {lastName1Error && (
                  <div className="text-red-400 text-xs mt-1 text-center">
                    {lastName1Error}
                  </div>
                )}
              </div>
              <div className="relative">
                <input
                  name="last_name2"
                  value={formData.last_name2 || ""}
                  onChange={handleChange}
                  placeholder={t.lastName2Placeholder}
                  className="bg-transparent border-b border-white p-1 text-white text-center w-full"
                />
                {lastName2Error && (
                  <div className="text-red-400 text-xs mt-1 text-center">
                    {lastName2Error}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-white text-lg font-semibold text-center">{fullName}</p>
          )}
          <span
            className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
              operator.status === "active"
                ? "bg-green-500 text-white"
                : operator.status === "pending"
                ? "bg-yellow-400 text-black"
                : "bg-red-500 text-white"
            }`}
          >
            {t[operator.status] || operator.status}
          </span>
        </div>

        {/* Información */}
        <div className="flex-1 text-white space-y-4">
          <div className="flex items-center justify-between border-b border-white/20 pb-2">
            <h2 className="text-3xl font-bold">{t.profileTitle}</h2>
            {!editMode ? (
              <button onClick={() => setEditMode(true)} className="text-green-400 hover:text-green-300">
                <FaEdit className="inline mr-1" /> {t.edit}
              </button>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={hasErrors()}
                  className={`px-3 py-1 rounded-lg shadow ${
                    hasErrors() 
                      ? "bg-gray-400 cursor-not-allowed" 
                      : "bg-green-400 text-black hover:bg-green-300"
                  }`}
                >
                  <FaSave className="inline mr-1" /> {t.save}
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-red-400 text-black px-3 py-1 rounded-lg shadow hover:bg-red-300"
                >
                  <FaTimes className="inline mr-1" /> {t.cancel}
                </button>
              </div>
            )}
          </div>

          {/* Datos */}
          <div className="flex items-center gap-3">
            <FaEnvelope className="text-green-400" />
            <span>{operator.email}</span>
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <FaPhone className="text-green-400" />
              {editMode ? (
                <input
                  name="phone"
                  value={formData.phone || ""}
                  onChange={handleChange}
                  className="bg-transparent border-b border-white w-full p-1 text-white"
                  placeholder={t.phonePlaceholder || "Ej: 8888-8888 o +506 8888-8888"}
                />
              ) : (
                <span>{operator.phone}</span>
              )}
            </div>
            {phoneError && (
              <div className="flex items-center gap-2 text-red-400 text-sm ml-7">
                <FaExclamationTriangle />
                <span>{phoneError}</span>
              </div>
            )}
            {editMode && !phoneError && (
              <div className="text-xs text-gray-300 ml-7">
                {t.phoneFormatHint || "Formato: 8888-8888, 50688888888 o +506 8888-8888"}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <FaMapMarkerAlt className="text-green-400" />
            {editMode ? (
              <input
                name="location"
                value={formData.location || ""}
                onChange={handleChange}
                className="bg-transparent border-b border-white w-full p-1 text-white"
                placeholder={t.locationPlaceholder}
              />
            ) : (
              <span>{operator.location}</span>
            )}
          </div>

          

          <div>
            <h3 className="text-sm font-bold text-green-400">{t.description}</h3>
            {editMode ? (
              <textarea
                name="description"
                value={formData.description || ""}
                onChange={handleChange}
                className="bg-transparent border border-white w-full p-2 rounded text-white"
                placeholder={t.descriptionPlaceholder}
              />
            ) : (
              <p className="text-sm text-gray-200">{operator.description}</p>
            )}
          </div>

          <div>
            <h3 className="text-sm font-bold text-green-400">{t.registrationDate}</h3>
            <p className="text-sm text-gray-300">
              {new Date(operator.registration_date).toLocaleDateString(language === 'es' ? 'es-CR' : 'en-US', {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          {operator.is_verified && (
            <div className="flex items-center gap-2 mt-4 text-green-400">
              <FaCheckCircle />
              <span>{t.verifiedAccount}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}